import { useMemo, useRef, useState } from 'react';

// material-ui
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import CardContent from '@mui/material/CardContent';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import FormHelperText from '@mui/material/FormHelperText';
import InputAdornment from '@mui/material/InputAdornment';
import InputLabel from '@mui/material/InputLabel';
import OutlinedInput from '@mui/material/OutlinedInput';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

// project imports
import Avatar from 'components/@extended/Avatar';
import MainCard from 'components/MainCard';
import Transitions from 'components/@extended/Transitions';
import IconButton from 'components/@extended/IconButton';
import { changePassword, logout } from 'services/api/login';
import { removeToken } from 'services/http';

// assets
import EyeInvisibleOutlined from '@ant-design/icons/EyeInvisibleOutlined';
import EyeOutlined from '@ant-design/icons/EyeOutlined';
import CloseOutlined from '@ant-design/icons/CloseOutlined';
import LockOutlined from '@ant-design/icons/LockOutlined';
import LogoutOutlined from '@ant-design/icons/LogoutOutlined';
import avatar1 from 'assets/images/users/avatar-1.png';

const INITIAL_PASSWORD_FORM = {
  old_pass: '',
  new_pass: '',
  repeat_new_pass: ''
};

// ==============================|| HEADER CONTENT - PROFILE ||============================== //

export default function Profile() {
  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [passwordForm, setPasswordForm] = useState(INITIAL_PASSWORD_FORM);
  const [passwordError, setPasswordError] = useState('');
  const [passwordSubmitting, setPasswordSubmitting] = useState(false);
  const [visiblePasswords, setVisiblePasswords] = useState({
    old_pass: false,
    new_pass: false,
    repeat_new_pass: false
  });

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const profileName = useMemo(() => {
    try {
      const info = localStorage.getItem('userInfo');
      return info ? JSON.parse(info)?.username || 'User' : 'User';
    } catch {
      return 'User';
    }
  }, []);

  const handleLogout = async () => {
    try {
      await logout();
    } catch {
      // 后端登出失败也清理本地登录态
    }
    removeToken();
    localStorage.removeItem('userInfo');
    window.location.href = '/login';
  };

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  const resetPasswordForm = () => {
    setPasswordForm(INITIAL_PASSWORD_FORM);
    setPasswordError('');
    setVisiblePasswords({ old_pass: false, new_pass: false, repeat_new_pass: false });
  };

  const handleOpenPasswordDialog = () => {
    setOpen(false);
    resetPasswordForm();
    setPasswordDialogOpen(true);
  };

  const handleClosePasswordDialog = (_event, reason) => {
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
    if (passwordSubmitting) return;
    setPasswordDialogOpen(false);
    resetPasswordForm();
  };

  const updatePasswordField = (field, value) => {
    setPasswordForm((previous) => ({ ...previous, [field]: value }));
    setPasswordError('');
  };

  const togglePasswordVisibility = (field) => {
    setVisiblePasswords((previous) => ({ ...previous, [field]: !previous[field] }));
  };

  const handleSubmitPassword = async () => {
    const { old_pass: oldPassword, new_pass: newPassword, repeat_new_pass: repeatNewPassword } = passwordForm;
    if (!oldPassword) {
      setPasswordError('请输入当前密码');
      return;
    }
    if (!newPassword) {
      setPasswordError('请输入新密码');
      return;
    }
    if (newPassword.length < 8) {
      setPasswordError('新密码至少需要 8 位');
      return;
    }
    if (!repeatNewPassword) {
      setPasswordError('请重复输入新密码');
      return;
    }
    if (newPassword !== repeatNewPassword) {
      setPasswordError('两次新密码不一致');
      return;
    }
    if (oldPassword === newPassword) {
      setPasswordError('新密码不能和当前密码相同');
      return;
    }

    setPasswordSubmitting(true);
    setPasswordError('');
    try {
      await changePassword(passwordForm);
      // 修改密码后服务端会撤销当前会话，立即清理本地登录态并要求重新登录。
      removeToken();
      window.location.href = '/login';
    } catch (error) {
      setPasswordError(error?.payload?.message || error?.message || '密码修改失败，请稍后重试');
    } finally {
      setPasswordSubmitting(false);
    }
  };

  const passwordFields = [
    { name: 'old_pass', label: '当前密码', autoComplete: 'current-password' },
    { name: 'new_pass', label: '新密码', autoComplete: 'new-password' },
    { name: 'repeat_new_pass', label: '重复新密码', autoComplete: 'new-password' }
  ];

  return (
    <Box sx={{ flexShrink: 0, ml: 'auto' }}>
      <Tooltip title="Profile" disableInteractive>
        <ButtonBase
          sx={(theme) => ({
            p: 0.25,
            borderRadius: 1,
            '&:focus-visible': { outline: `2px solid ${theme.vars.palette.secondary.dark}`, outlineOffset: 2 }
          })}
          aria-label="open profile"
          ref={anchorRef}
          aria-controls={open ? 'profile-grow' : undefined}
          aria-haspopup="true"
          onClick={handleToggle}
        >
          <Avatar alt="profile user" src={avatar1} size="sm" sx={{ '&:hover': { outline: '1px solid', outlineColor: 'primary.main' } }} />
        </ButtonBase>
      </Tooltip>
      <Popper
        placement="bottom-end"
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        popperOptions={{
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: [0, 9]
              }
            }
          ]
        }}
      >
        {({ TransitionProps }) => (
          <Transitions type="grow" position="top-right" in={open} {...TransitionProps}>
            <Paper sx={(theme) => ({ boxShadow: theme.vars.customShadows.z1, width: 290, minWidth: 240, maxWidth: { xs: 250, md: 290 } })}>
              <ClickAwayListener onClickAway={handleClose}>
                <MainCard elevation={0} border={false} content={false}>
                  <CardContent sx={{ px: 2.5, pt: 3, pb: 2.5 }}>
                    <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
                      <Stack direction="row" sx={{ gap: 1.25, alignItems: 'center' }}>
                        <Avatar alt="profile user" src={avatar1} sx={{ width: 32, height: 32 }} />
                        <Stack>
                          <Typography variant="h6">{profileName}</Typography>
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            已登录
                          </Typography>
                        </Stack>
                      </Stack>
                      <Tooltip title="退出登录">
                        <IconButton size="large" sx={{ color: 'text.primary' }} onClick={handleLogout}>
                          <LogoutOutlined />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                    <Divider sx={{ my: 2 }} />
                    <Button fullWidth variant="outlined" startIcon={<LockOutlined />} onClick={handleOpenPasswordDialog}>
                      修改密码
                    </Button>
                  </CardContent>
                </MainCard>
              </ClickAwayListener>
            </Paper>
          </Transitions>
        )}
      </Popper>

      <Dialog open={passwordDialogOpen} onClose={handleClosePasswordDialog} disableEscapeKeyDown fullWidth maxWidth="xs">
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1.5 }}>
          修改密码
          <IconButton aria-label="关闭修改密码弹窗" onClick={handleClosePasswordDialog} disabled={passwordSubmitting} size="small">
            <CloseOutlined />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            {passwordError && <Alert severity="error">{passwordError}</Alert>}
            {passwordFields.map(({ name, label, autoComplete }) => (
              <Stack key={name} spacing={0.75}>
                <InputLabel htmlFor={`change-password-${name}`}>{label}</InputLabel>
                <OutlinedInput
                  id={`change-password-${name}`}
                  type={visiblePasswords[name] ? 'text' : 'password'}
                  value={passwordForm[name]}
                  onChange={(event) => updatePasswordField(name, event.target.value)}
                  autoComplete={autoComplete}
                  fullWidth
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton
                        aria-label={`切换${label}可见状态`}
                        onClick={() => togglePasswordVisibility(name)}
                        onMouseDown={(event) => event.preventDefault()}
                        edge="end"
                        color="secondary"
                      >
                        {visiblePasswords[name] ? <EyeOutlined /> : <EyeInvisibleOutlined />}
                      </IconButton>
                    </InputAdornment>
                  }
                />
              </Stack>
            ))}
            <FormHelperText>新密码至少需要 8 位，且不能与当前密码相同。</FormHelperText>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="contained" onClick={handleSubmitPassword} disabled={passwordSubmitting}>
            {passwordSubmitting ? '提交中...' : '确认修改'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
